import React, { useCallback, useEffect, useRef, useState } from "react";
import config from "./config";
import { reportOffers } from "./Plans";
import "./CreditCheckout.css";

const apiBase = config.API_URL.replace(/\/+$/, "");

export default function CreditCheckout({ result }) {
  const offerKey = new URLSearchParams(window.location.search).get("offer") || "credit1";
  const offer = reportOffers.find(item => item.key === offerKey);
  const token = localStorage.getItem("token");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [authExpired, setAuthExpired] = useState(false);
  const started = useRef(false);
  const inFlight = useRef(false);

  const startCheckout = useCallback(async () => {
    if (inFlight.current || !offer || !token) return;
    inFlight.current = true;
    setBusy(true);
    setError("");
    setAuthExpired(false);
    try {
      const checkout = await fetch(`${apiBase}/api/billing/credits/checkout?offer=${encodeURIComponent(offerKey)}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (checkout.status === 401) {
        setAuthExpired(true);
        throw new Error("Your website session has expired. Sign in again, then choose this plan.");
      }
      if (!checkout.ok) {
        const failure = await checkout.json().catch(() => ({}));
        throw new Error(failure.message || "Checkout is unavailable. Please try again.");
      }
      const session = await checkout.json();
      const url = new URL(session.checkoutUrl);
      if (url.protocol !== "https:" || url.hostname !== "checkout.stripe.com")
        throw new Error("Checkout returned an invalid destination.");
      window.location.assign(url.href);
    } catch (reason) {
      setError(reason.message || "Unable to start checkout. Please try again.");
      setBusy(false);
      inFlight.current = false;
    }
  }, [offer, offerKey, token]);

  useEffect(() => {
    if (!result && !started.current) {
      started.current = true;
      startCheckout();
    }
  }, [result, startCheckout]);

  if (result) {
    return <main className="credit-checkout">
      <h1>{result === "success" ? "Payment submitted" : "Checkout canceled"}</h1>
      <p>{result === "success"
        ? "Your credits or monthly allowance appear after payment is confirmed. Return to the desktop app to refresh your balance."
        : "No credit was purchased. You can return to the desktop app and try again."}</p>
      <a href="/plans">Back to plans</a>
    </main>;
  }

  return <main className="credit-checkout">
    <h1>{offer ? `Purchase ${offer.name}` : "Unknown plan"}</h1>
    {!offer && <p role="alert">Choose a product from <a href="/plans">Plans</a>.</p>}
    {offer?.monthly && <p>10 reports each paid month, with no rollover. Monthly reports are used first; purchased credits remain available after renewal. Renews automatically until canceled.</p>}
    {!token && offer && <p role="alert">Sign in to the website, then choose this plan again.</p>}
    {busy && <p>Opening Stripe checkout...</p>}
    {error && <p role="alert" className="credit-checkout-error">{error}</p>}
    {error && !authExpired && token && offer && <button type="button" onClick={startCheckout}>Retry checkout</button>}
  </main>;
}
